import { Shield, MapPin, Users, CheckCircle } from 'lucide-react';
import { Footer } from '@/sections/Footer';
import { SEO } from '@/components/SEO';
import { useReveal } from '@/lib/useReveal';
import { Section, Container, Display, Lead, Pill, CtaLink, Tile } from '@/components/system';

const benefits = [
  { icon: Shield, title: 'Verificación completa.', text: 'Documentación legal vigente, ubicación física verificable y actividad auditada dentro del sistema.' },
  { icon: MapPin, title: 'Cobertura para toda Cuba.', text: 'Las 15 provincias y los 168 municipios soportados desde el primer día.' },
  { icon: Users, title: 'Soporte dedicado.', text: 'El equipo MEC acompaña a cada socio en el onboarding y en cada etapa de crecimiento.' },
];

const requirements = [
  'Documentación legal vigente',
  'Ubicación física verificable',
  'Capacidad de procesamiento de envíos',
  'Compromiso con estándares de calidad',
];

export function Socios() {
  const heroRef = useReveal<HTMLElement>();
  const foundersRef = useReveal<HTMLElement>();
  const benefitsRef = useReveal<HTMLElement>();
  const closerRef = useReveal<HTMLElement>();

  return (
    <main className="bg-bg-light">
      <SEO
        title="Socios | Red de Agencias Verificadas | Mi Envío Cuba"
        description="La red de agencias verificadas que operan con MEC: documentación legal, ubicación física verificable y actividad auditada. Únete como agencia fundadora."
        path="/socios"
        keywords="agencias verificadas envíos Cuba, red de agencias MEC, socios Mi Envío Cuba, agencia fundadora"
      />

      {/* Hero */}
      <Section tone="white" ref={heroRef} className="pt-[120px] sm:pt-[150px] pb-16 sm:pb-20">
        <Container>
          <div className="mx-auto max-w-4xl text-center">
            <p data-reveal className="text-brand font-semibold text-base sm:text-lg mb-4">Socios</p>
            <Display data-reveal as="h1" size="xl">Una red que se gana la confianza.</Display>
            <Lead data-reveal className="mt-6 mx-auto max-w-2xl">
              Cada agencia que opera con MEC pasa un proceso de verificación — porque del otro
              lado de cada paquete hay una familia esperando.
            </Lead>
            <div data-reveal className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
              <Pill to="/plataforma#solicitar">Convertirse en socio</Pill>
              <CtaLink to="/plataforma">Conoce la plataforma</CtaLink>
            </div>
          </div>
        </Container>
      </Section>

      {/* Founding agencies */}
      <Section tone="dark" ref={foundersRef} className="py-16 sm:py-24">
        <Container>
          <div className="mx-auto max-w-3xl text-center">
            <p data-reveal className="inline-flex items-center rounded-full bg-white/10 px-4 py-1.5 text-xs font-semibold text-text-light mb-6">
              Red en formación
            </p>
            <Display data-reveal size="md" className="text-white">Agencias fundadoras.</Display>
            <Lead data-reveal dark className="mt-6">
              Estamos incorporando las agencias fundadoras de la red MEC. Conocerás públicamente
              a los socios a medida que la red crezca — cada uno, verificado antes de operar.
            </Lead>
            <ul data-reveal className="mt-10 grid sm:grid-cols-2 gap-3 text-left">
              {requirements.map((r) => (
                <li key={r} className="flex items-center gap-3 rounded-2xl bg-white/5 px-5 py-4">
                  <CheckCircle className="w-5 h-5 text-brand-light shrink-0" />
                  <span className="text-text-light font-medium">{r}</span>
                </li>
              ))}
            </ul>
          </div>
        </Container>
      </Section>

      {/* Benefits */}
      <Section tone="grey" ref={benefitsRef} className="py-16 sm:py-20">
        <Container>
          <Display data-reveal size="md" className="mb-10">Qué recibe un socio.</Display>
          <div className="grid sm:grid-cols-3 gap-4">
            {benefits.map((b) => (
              <Tile key={b.title} data-reveal className="p-7 sm:p-8">
                <div className="w-11 h-11 rounded-xl bg-brand/10 flex items-center justify-center">
                  <b.icon className="w-5 h-5 text-brand" />
                </div>
                <h3 className="mt-5 font-heading text-xl font-semibold tracking-tight">{b.title}</h3>
                <p className="mt-2 text-text-secondary leading-relaxed">{b.text}</p>
              </Tile>
            ))}
          </div>
        </Container>
      </Section>

      {/* Closer */}
      <Section tone="grey" ref={closerRef} className="pt-0 pb-16 sm:pb-20">
        <Container>
          <div className="rounded-[32px] bg-white px-8 py-16 sm:px-12 sm:py-20 text-center">
            <Display data-reveal size="md">¿Quieres ser socio?</Display>
            <Lead data-reveal className="mt-5 mx-auto max-w-2xl">
              Únete a la red de agencias verificadas: plataforma completa, presencia en la app
              Mi Envío Cuba y soporte dedicado.
            </Lead>
            <div data-reveal className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
              <Pill to="/plataforma#solicitar">Convertirse en socio</Pill>
              <CtaLink to="/plataforma#planes">Ver planes y precios</CtaLink>
            </div>
          </div>
        </Container>
      </Section>

      <Footer />
    </main>
  );
}
